if (!customElements.get('gb-pad-variant-content')) {
  customElements.define('gb-pad-variant-content', class extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      this.initialView ??= new URL(window.location.href).searchParams.get('view') || '';
      this.content = this.querySelector('[data-gb-content]');
      this.templates = new Map(Array.from(this.querySelectorAll('template[data-gb-variant-id]'), (node) => [String(node.dataset.gbVariantId), node]));
      this.fallback = this.querySelector('template[data-gb-fallback]');
      try {
        this.variants = JSON.parse(this.querySelector('[data-gb-variants]')?.textContent || '[]');
      } catch {
        this.variants = [];
      }
      if (!Array.isArray(this.variants)) this.variants = [];
      this.variants = this.variants.filter((variant) => variant?.id && Array.isArray(variant.options));
      this.status = this.querySelector('[data-gb-status]');
      if (!this.status) {
        this.status = document.createElement('p');
        this.status.dataset.gbStatus = '';
        this.status.className = 'visually-hidden';
        this.status.setAttribute('role', 'status');
        this.append(this.status);
      }
      this.currentId = String(this.dataset.initialVariantId || '');
      this.expectedId = this.currentId || null;
      this.unsubscribers = [];
      const signal = this.lifecycle.signal;
      document.addEventListener('change', (event) => this.onFormChange(event), { signal });
      window.addEventListener('popstate', () => this.restoreHistory(), { signal });
      window.addEventListener('pageshow', () => this.restoreHistory(), { signal });
      customElements.whenDefined('product-info').then(() => {
        if (!this.isConnected || signal.aborted) return;
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => this.onSelection(event)));
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariant(event)));
        const id = this.formVariantId;
        this.expectedId = id || null;
        this.renderVariant(id, null, false);
      });
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = null;
      this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
      clearTimeout(this.loadingTimer);
    }

    get main() {
      return Array.from(document.querySelectorAll('product-info[id^="MainProduct-"]')).find((node) => node.dataset.productId === this.dataset.productId);
    }

    get formVariantId() {
      const main = this.main;
      return String(main?.querySelector(`form[id="product-form-${main.dataset.section}"] input[name="id"]`)?.value || '');
    }

    get groups() {
      return Array.from(this.main?.querySelector('variant-selects')?.querySelectorAll('fieldset, .product-form__input--dropdown') || []);
    }

    selectedOptions() {
      return this.groups.map((group) => (group.querySelector('input:checked') || group.querySelector('select')?.selectedOptions[0])?.value || '');
    }

    variantForOptions(options) {
      return this.variants.find((variant) => variant.options.length === options.length && variant.options.every((value, index) => String(value) === options[index]));
    }

    onSelection({ data }) {
      if (!this.main?.contains(data?.event?.target)) return;
      this.expectedId = this.variantForOptions(this.selectedOptions())?.id?.toString() || null;
      this.pending = true;
      this.showFallback(this.dataset.loadingMessage, true);
      clearTimeout(this.loadingTimer);
      this.loadingTimer = setTimeout(() => {
        this.pending = false;
        this.showFallback(this.dataset.unavailableMessage, false);
      }, 15000);
    }

    onFormChange(event) {
      const main = this.main;
      if (event.target?.name !== 'id' || !main?.contains(event.target)) return;
      if (this.initialView && main.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href);
        if (url.pathname === new URL(main.dataset.url, url.origin).pathname && url.searchParams.get('view') !== this.initialView) {
          url.searchParams.set('view', this.initialView);
          window.history.replaceState(window.history.state, '', url);
        }
      }
      if (event.target.value || this.expectedId) return;
      this.renderVariant('', null, true);
    }

    onVariant({ data }) {
      const main = this.main;
      if (!main || String(data?.sectionId) !== String(main.dataset.originalSection || main.dataset.section)) return;
      const id = String(data.variant?.id || '');
      if (id !== String(this.expectedId || '') || id !== this.formVariantId) return;
      this.renderVariant(id, data.html, true);
    }

    replaceContent(template) {
      if (!this.content) return;
      const focusedId = this.content.contains(document.activeElement) ? document.activeElement.id : '';
      this.content.replaceChildren(...(template ? [template.content.cloneNode(true)] : []));
      if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
    }

    updateSummary(name, summary) {
      const main = this.main;
      main?.querySelectorAll('[data-gb-selected-compound]').forEach((node) => {
        node.textContent = name ? `${this.dataset.selectedLabel}: ${name}` : this.dataset.selectedLabel;
      });
      main?.querySelectorAll('[data-gb-compound-summary]').forEach((node) => { node.textContent = summary || ''; });
    }

    showFallback(message, loading) {
      this.replaceContent(this.fallback);
      this.currentId = '';
      this.content?.setAttribute('aria-busy', String(loading));
      this.updateSummary('', message);
      this.status.textContent = message || '';
      this.updateSticky(null, true);
    }

    renderVariant(id, source, announce) {
      clearTimeout(this.loadingTimer);
      this.pending = false;
      const template = this.templates.get(String(id));
      if (!template || !template.dataset.compoundName) {
        this.showFallback(this.dataset.unavailableMessage, false);
        if (id) this.updateSticky(source, false);
      } else {
        if (this.currentId !== String(id)) this.replaceContent(template);
        this.currentId = String(id);
        this.content?.setAttribute('aria-busy', 'false');
        this.updateSummary(template.dataset.compoundName, template.dataset.compoundSummary);
        this.status.textContent = announce ? `${this.dataset.selectedLabel}: ${template.dataset.compoundName}` : '';
        this.updateSticky(source, false);
      }
      if (this.historyFocus?.isConnected) {
        const focused = this.historyFocus;
        queueMicrotask(() => { if (focused.isConnected) focused.focus({ preventScroll: true }); });
      }
      this.historyFocus = null;
    }

    updateSticky(source, forceDisabled) {
      const main = this.main;
      const sticky = main?.querySelector('.gb-mobile-atc');
      if (!sticky) return;
      const button = sticky.querySelector('button[type="submit"]');
      const nativeButton = main.querySelector(`button[id="ProductSubmitButton-${main.dataset.section}"]`);
      if (button) {
        button.disabled = forceDisabled || !nativeButton || nativeButton.disabled;
        button.setAttribute('aria-disabled', String(button.disabled));
        if (!forceDisabled) {
          const label = nativeButton?.querySelector('span')?.textContent?.trim();
          if (label) button.textContent = label;
        }
      }
      const price = sticky.querySelector('.gb-mobile-atc__price');
      if (!price) return;
      if (forceDisabled) {
        price.textContent = '';
      } else if (source) {
        const sourceMain = Array.from(source.querySelectorAll('product-info')).find((node) => node.dataset.productId === this.dataset.productId);
        const updatedPrice = sourceMain?.querySelector('.gb-mobile-atc__price');
        price.textContent = updatedPrice?.textContent || '';
      } else {
        const nativePrice = main.querySelector(`[id="price-${main.dataset.section}"]`);
        const currentPrice = nativePrice?.querySelector('.price--on-sale .price__sale .price-item--sale') || nativePrice?.querySelector('.price__regular .price-item--regular');
        if (currentPrice) price.textContent = currentPrice.textContent.trim();
      }
    }

    restoreHistory() {
      const main = this.main;
      if (!main || !this.isConnected) return;
      const url = new URL(window.location.href);
      if (url.pathname !== new URL(main.dataset.url, url.origin).pathname) return;
      const optionIds = url.searchParams.get('option_values')?.split(',');
      const requestedId = url.searchParams.get('variant') || this.dataset.defaultVariantId || this.dataset.initialVariantId;
      const variant = this.variants.find((item) => String(item.id) === String(requestedId));
      const groups = this.groups;
      if (!groups.length && this.variants.length === 1 && !optionIds && variant && String(variant.id) === this.formVariantId) {
        this.expectedId = String(variant.id);
        this.renderVariant(this.expectedId, null, false);
        return;
      }
      const targets = groups.map((group, index) => Array.from(group.querySelectorAll('input[type="radio"], select option')).find((option) => optionIds ? option.dataset.optionValueId === optionIds[index] : option.value === String(variant?.options[index] ?? '')));
      if (!groups.length || targets.some((target) => !target) || (!optionIds && !variant)) {
        this.expectedId = null;
        this.showFallback(this.dataset.unavailableMessage, false);
        return;
      }
      const options = targets.map((target) => target.value);
      const id = this.variantForOptions(options)?.id?.toString() || '';
      const currentOptions = this.selectedOptions();
      if (!this.pending && id === this.formVariantId && options.every((value, index) => value === currentOptions[index])) {
        this.expectedId = id || null;
        this.renderVariant(id, null, false);
        return;
      }
      this.historyFocus = document.activeElement;
      let changeTarget;
      targets.forEach((target) => {
        if (target.tagName === 'OPTION') {
          const select = target.closest('select');
          select.value = target.value;
          Array.from(select.options).forEach((option) => option.toggleAttribute('selected', option === target));
          changeTarget = select;
        } else {
          target.checked = true;
          changeTarget = target;
        }
      });
      changeTarget?.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
}
